import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { ExternalLinkIcon, ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
// ─── Types ───────────────────────────────────────────────────────────────────
interface SubLink {
  label: string;
  url: string;
}
interface CaseStudy {
  id: string;
  title: string;
  client: string;
  subtitle: string;
  highlight: string;
  url: string;
  image: string;
  subLinks?: SubLink[];
  livePrototype?: boolean;
}
interface TabSection {
  id: string;
  label: string;
  studies: CaseStudy[];
}
// ─── Data ────────────────────────────────────────────────────────────────────
const TABS: TabSection[] = [{
  id: 'enterprise',
  label: 'Enterprise & Retail UX',
  studies: [{
    id: 'ashley',
    title: 'Ashley Furniture eCommerce Replatforming',
    client: 'Ashley Furniture',
    subtitle: 'Replatforming & UX Design',
    highlight: "Led end-to-end UX for a $1B+ retailer's digital transformation",
    url: 'https://www.behance.net/gallery/155690833/Ashley-Furniture-eCommerce-Site-Redesign',
    image: 'https://cdn.magicpatterns.com/uploads/qkEjmJcopVR6NcFTbGx6e9/Screenshot_2026-02-25_at_1.21.33_PM.png'
  }, {
    id: 'saloncentric',
    title: 'SalonCentric Product Discovery & Shop Owner Checkout',
    client: "SalonCentric / L'Oréal",
    subtitle: 'Retail UX/UI',
    highlight: 'Redesigned B2B checkout flow for 30,000+ professional salon owners',
    url: 'https://www.behance.net/gallery/228107425/SalonCentric-LOral-UX-UI-Strategy-Design',
    image: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=800&q=80'
  }, {
    id: 'techdata',
    title: 'Tech Data E-Commerce Platform',
    client: 'Tech Data',
    subtitle: 'E-Commerce UX/UI',
    highlight: 'UX/UI design for a global technology distribution e-commerce platform',
    url: 'https://www.behance.net/gallery/67132103/Tech-Data-UXUI-Design-E-Commerce-Platform',
    image: 'https://mir-s3-cdn-cf.behance.net/project_modules/hd_webp/d0334e67132103.5b2f0e0c62cf5.jpg'
  }, {
    id: 'techdata-india',
    title: 'Tech Data E-Commerce — India Branch',
    client: 'Tech Data India',
    subtitle: 'Desktop & Mobile UX/UI',
    highlight: "Localized e-commerce UX/UI design for Tech Data's India market expansion",
    url: 'https://www.behance.net/gallery/63214219/eCommerce-DesktopMobile-UX-UI-Design',
    image: 'https://mir-s3-cdn-cf.behance.net/project_modules/fs/a289f963214219.5aa95748202c5.png'
  }]
}, {
  id: 'government',
  label: 'Government & Public Sector',
  studies: [{
    id: 'wh-offboarding',
    title: 'White House Virtual Employee Off-boarding',
    client: 'Executive Office of the President',
    subtitle: 'GovTech UX/UI',
    highlight: 'Live interactive prototype — modernized federal HR out-processing portal',
    url: 'https://project-out-processing-portal-dashboard-690.magicpatterns.app/',
    image: 'https://cdn.magicpatterns.com/uploads/7CiutdBPeGnZ1qeeVim9Fi/Screenshot_2026-01-15_at_6.34.48_PM.png',
    livePrototype: true
  }, {
    id: 'eop-portal',
    title: 'Executive Office Organization Portal',
    client: 'Executive Office of the President',
    subtitle: 'Intranet UX/UI',
    highlight: 'Live interactive prototype — unified EOP staff intranet across agencies',
    url: 'https://www.magicpatterns.com/c/bz92czqedxjqfer4sqqutt/preview',
    image: 'https://cdn.magicpatterns.com/uploads/fcJXtwwL1AB9gbqZQUc8j4/Screenshot_2026-02-25_at_1.24.52_PM.png',
    livePrototype: true
  }, {
    id: 'wh-tours',
    title: 'White House Tours: Product Design UX & UI',
    client: 'Executive Office of the President',
    subtitle: 'Public-Facing UX',
    highlight: 'Live interactive prototype — reimagined public tour booking experience',
    url: 'https://project-white-house-tours-booking-experience-350.magicpatterns.app/onboarding',
    image: 'https://cdn.magicpatterns.com/uploads/629JZZZfVHFMRWSVa3KP6C/Screenshot_2026-02-02_at_10.09.42_AM.png',
    livePrototype: true
  }, {
    id: 'kadiak',
    title: 'Kadiak — Koniag Government Services',
    client: 'Koniag / Dept. of Interior',
    subtitle: 'UX/UI & Info Design',
    highlight: 'UX, UI, and information design for a federal government services contractor',
    url: 'https://www.behance.net/gallery/228457555/Koniag-UX-UI-Information-Design',
    image: 'https://cdn.magicpatterns.com/uploads/xqWBqZKyMQMw8qDDJkQFot/Screenshot_2026-02-25_at_1.21.05_PM.png'
  }, {
    id: 'ussocom',
    title: 'USSOCOM — CACI',
    client: 'US Special Operations Command',
    subtitle: 'Defense UX/UI',
    highlight: 'Visual design and information systems for US Special Operations Command convention',
    url: 'https://www.behance.net/gallery/12054117/US-Special-Operations-Command-ISOF-Convention',
    image: 'https://cdn.magicpatterns.com/uploads/nLU2P9WoxUSZTtaKowv4eh/Screenshot_2026-02-25_at_1.14.26_PM.png'
  }, {
    id: 'arma-global',
    title: 'ARMA Global — GDIT',
    client: 'ARMA Global / General Dynamics IT',
    subtitle: 'Branding & Collateral',
    highlight: 'Brand identity and collateral design for a defense IT services organization',
    url: 'https://www.behance.net/gallery/30374241/ARMA-Global-Branding-Collateral',
    image: 'https://cdn.magicpatterns.com/uploads/hGSru6YQCv6fpuEkJNYwhC/37b06c30374241.561fcb00ce29b.png'
  }, {
    id: 'spathe',
    title: 'Spathe Systems — C4ISR Contracting',
    client: 'Spathe Systems',
    subtitle: 'Defense Brand & UX',
    highlight: 'Brand and UX design for a C4ISR defense technology contracting firm',
    url: 'https://www.behance.net/gallery/225355389/Spathe-System-Solutions',
    image: 'https://cdn.magicpatterns.com/uploads/nrEeCcqeB48vnHdN5Ja9W4/Screenshot_2026-02-25_at_1.22.16_PM.png'
  }]
}, {
  id: 'brand',
  label: 'Brand & Campaign Design',
  studies: [{
    id: 'walmart-brands',
    title: 'National & International Brands',
    client: 'Walmart.com & Global Retail',
    subtitle: 'Brand Strategy',
    highlight: 'Visual identity and brand systems for national and international retail at scale',
    url: 'https://www.behance.net/gallery/17416611/Walmartcom-Various',
    image: 'https://cdn.magicpatterns.com/uploads/p51YL8WoqXrvsQDyKrfHkU/Screenshot_2026-02-25_at_1.16.38_PM.png'
  }, {
    id: 'achq',
    title: 'ACHQ — Payment Processing & Financial Mgmt.',
    client: 'ACHQ.com',
    subtitle: 'UX/UI & Development',
    highlight: 'Full site UX/UI design and development for a fintech payment platform',
    url: 'https://www.behance.net/gallery/43795235/ACHQcom-Official-Site-UXUI-Design-Development',
    image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&q=80'
  }, {
    id: 'veronica-lane',
    title: 'Veronica Lane PMP Consulting',
    client: 'Veronica Lane Consulting',
    subtitle: 'Rebrand & Identity',
    highlight: 'Project management consultancy rebrand — positioning, identity, and digital presence',
    url: 'https://www.behance.net/gallery/125955901/Project-Mgmt-Rebrand-ProjectVeronica-Lane-COnsulting',
    image: 'https://cdn.magicpatterns.com/uploads/bpN79Ej1RcGYWHwQrLaDss/Screenshot_2026-02-25_at_1.14.51_PM.png'
  }, {
    id: 'sports-spa',
    title: 'Optimal Performance — Personal Training & Wellness',
    client: 'The Sports Spa',
    subtitle: 'Brand Identity',
    highlight: 'Brand identity system for a premium personal training and wellness studio',
    url: 'https://www.behance.net/gallery/155537303/The-Sports-Spa-Brand-Identity-Project-(beta)',
    image: 'https://cdn.magicpatterns.com/uploads/kyuEFvVQ8RqXqSnLaK7XrN/Screenshot_2026-02-25_at_1.15.33_PM.png'
  }, {
    id: 'rtm-nation',
    title: 'RTM Nation Campaign',
    client: 'RTM Nation',
    subtitle: 'Campaign & Launch',
    highlight: 'Concept campaign launch — brand narrative, visual identity, and go-to-market assets',
    url: 'https://www.behance.net/gallery/53094079/RTM-Nation-Campaign-Concept-Launch-Enter-Into-The-New',
    image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&q=80'
  }, {
    id: 'switch-conference',
    title: 'The Switch Conference Branding',
    client: 'The Switch Conference',
    subtitle: 'Event Branding',
    highlight: 'Full brand system for a professional conference — identity, collateral, and digital',
    url: 'https://www.behance.net/gallery/81864379/The-Switch-Conference-Branding',
    image: 'https://cdn.magicpatterns.com/uploads/eX9WhsphYEAbo57TFiNMVY/Screenshot_2026-02-25_at_1.16.02_PM.png'
  }, {
    id: 'phoenix-hills',
    title: 'Phoenix Hills Equestrian Estates',
    client: 'Phoenix Hills',
    subtitle: 'Luxury Brand Identity',
    highlight: 'Premium brand identity for a luxury equestrian residential development',
    url: 'https://www.behance.net/gallery/125933423/Phoenix-Hills-Equestrian-Estates',
    image: 'https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=800&q=80'
  }, {
    id: 'art-of-war',
    title: 'Art of War Series',
    client: 'Independent Series',
    subtitle: 'Editorial Design',
    highlight: 'Editorial design series — visual storytelling and conceptual art direction',
    url: 'https://www.behance.net/gallery/85562329/Art-of-War-Series',
    image: 'https://cdn.magicpatterns.com/uploads/vHx68ienwBUcY1aN2KVYVe/Screenshot_2026-02-25_at_1.15.48_PM.png'
  }]
}];
// ─── Card Component ───────────────────────────────────────────────────────────
interface CaseStudyCardProps {
  study: CaseStudy;
  index: number;
}
function CaseStudyCard({
  study,
  index
}: CaseStudyCardProps) {
  const [hovered, setHovered] = useState(false);
  const handleCardClick = () => {
    window.open(study.url, '_blank', 'noopener,noreferrer');
  };
  const handleSubLinkClick = (e: React.MouseEvent, url: string) => {
    e.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  return <motion.article initial={{
    opacity: 0,
    y: 28
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.45,
    delay: index * 0.1,
    ease: [0.22, 1, 0.36, 1]
  }} whileHover={{
    scale: 1.025
  }} onHoverStart={() => setHovered(true)} onHoverEnd={() => setHovered(false)} onClick={handleCardClick} role="link" tabIndex={0} onKeyDown={e => e.key === 'Enter' && handleCardClick()} aria-label={`View case study: ${study.title}`} className="relative rounded-xl overflow-hidden cursor-pointer group shadow-md hover:shadow-xl transition-shadow duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50" style={{
    transition: 'box-shadow 0.3s ease'
  }} data-id="element-140">
      {/* Image */}
      <div className="relative aspect-video overflow-hidden bg-neutral-800" data-id="element-141">
        <motion.img src={study.image} alt={study.title} className="w-full h-full object-cover" animate={{
        scale: hovered ? 1.06 : 1
      }} transition={{
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-142" />

        {/* Permanent gradient overlay at bottom */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" data-id="element-143" />

        {/* Hover highlight overlay */}
        <AnimatePresence data-id="element-144">
          {hovered && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} className="absolute inset-0 bg-black/50 flex items-center justify-center px-6" data-id="element-145">
              <p className="text-white text-center text-sm font-medium leading-snug tracking-wide" data-id="element-146">
                {study.highlight}
              </p>
            </motion.div>}
        </AnimatePresence>

        {/* Bottom info bar */}
        <div className="absolute bottom-0 left-0 right-0 p-4" data-id="element-147">
          <div className="flex items-end justify-between gap-2" data-id="element-148">
            <div className="min-w-0" data-id="element-149">
              <p className="text-white/70 text-xs font-medium uppercase tracking-widest mb-1 truncate" data-id="element-150">
                {study.client}
              </p>
              <h3 className="text-white font-semibold text-sm leading-snug line-clamp-2" data-id="element-151">
                {study.title}
              </h3>
            </div>
            <div className="flex flex-col items-end gap-1.5 flex-shrink-0" data-id="element-152">
              {study.livePrototype && <span className="inline-flex items-center gap-1 bg-[#B08D57]/90 text-[#1C1F23] text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap" data-id="element-153">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1C1F23]/60 animate-pulse" data-id="element-154" />
                  Live Prototype
                </span>}
              <span className="inline-flex items-center gap-1 bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap" data-id="element-155">
                {study.subtitle}
              </span>
            </div>
          </div>
        </div>

        {/* External link icon on hover */}
        <motion.div className="absolute top-3 right-3" animate={{
        opacity: hovered ? 1 : 0,
        y: hovered ? 0 : -4
      }} transition={{
        duration: 0.2
      }} data-id="element-156">
          <div className="bg-white/15 backdrop-blur-sm rounded-full p-1.5" data-id="element-157">
            <ExternalLinkIcon className="w-3.5 h-3.5 text-white" data-id="element-158" />
          </div>
        </motion.div>
      </div>

      {/* Sub-links for branding card */}
      {study.subLinks && study.subLinks.length > 0 && <div className="bg-neutral-900 border-t border-white/10 px-4 py-3" data-id="element-159">
          <p className="text-white/50 text-xs uppercase tracking-widest mb-2 font-medium" data-id="element-160">
            Projects in this series
          </p>
          <div className="flex flex-col gap-1.5" data-id="element-161">
            {study.subLinks.map(link => <button key={link.url} onClick={e => handleSubLinkClick(e, link.url)} className="flex items-center gap-1.5 text-white/70 hover:text-white text-xs text-left transition-colors duration-150 group/link" data-id="element-162">
                <ExternalLinkIcon className="w-3 h-3 flex-shrink-0 text-white/40 group-hover/link:text-white/70 transition-colors" data-id="element-163" />
                <span className="hover:underline underline-offset-2" data-id="element-164">
                  {link.label}
                </span>
              </button>)}
          </div>
        </div>}
    </motion.article>;
}
// ─── Main Component ───────────────────────────────────────────────────────────
export const CaseStudies = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [dragStartX, setDragStartX] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, {
    once: true,
    margin: '-80px'
  });
  const activeSection = TABS[activeTab];
  // Reset carousel when tab changes
  const handleTabChange = (index: number) => {
    setActiveTab(index);
    setCarouselIndex(0);
  };
  // Cards per view (responsive)
  const getCardsPerView = () => {
    if (typeof window === 'undefined') return 2;
    return window.innerWidth >= 768 ? 2 : 1;
  };
  const [cardsPerView, setCardsPerView] = useState(getCardsPerView());
  useEffect(() => {
    const handleResize = () => setCardsPerView(getCardsPerView());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const totalCards = activeSection.studies.length;
  const maxIndex = Math.max(0, totalCards - cardsPerView);
  const handlePrev = () => {
    setCarouselIndex(prev => Math.max(0, prev - 1));
  };
  const handleNext = () => {
    setCarouselIndex(prev => Math.min(maxIndex, prev + 1));
  };
  const handleDragEnd = (_: any, info: any) => {
    const threshold = 50;
    if (info.offset.x > threshold && carouselIndex > 0) {
      handlePrev();
    } else if (info.offset.x < -threshold && carouselIndex < maxIndex) {
      handleNext();
    }
  };
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('keydown', handleKeyDown);
      return () => carousel.removeEventListener('keydown', handleKeyDown);
    }
  }, [carouselIndex, maxIndex]);
  const cardWidth = cardsPerView === 1 ? 100 : 50;
  const gap = cardsPerView === 1 ? 0 : 1.5; // 1.5% gap on desktop
  const translateX = -(carouselIndex * (cardWidth + gap));
  return <section ref={sectionRef} id="case-studies" className="w-full py-20 md:py-28 px-4 bg-neutral-950" aria-labelledby="case-studies-heading" data-id="element-165">
      <div className="max-w-5xl mx-auto" data-id="element-166">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} className="mb-12 md:mb-16" data-id="element-167">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-3" data-id="element-168">
            Portfolio
          </p>
          <h2 id="case-studies-heading" className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4" data-id="element-169">
            Featured Case Studies
          </h2>
          <p className="text-white/50 text-base md:text-lg max-w-xl" data-id="element-170">
            Selected work across enterprise, government, and brand
          </p>
        </motion.div>

        {/* Tabs */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} className="relative mb-10" role="tablist" aria-label="Case study categories" data-id="element-171">
          {/* Tab row */}
          <div className="flex gap-0 border-b border-white/10 overflow-x-auto scrollbar-none" data-id="element-172">
            {TABS.map((tab, i) => <button key={tab.id} role="tab" aria-selected={activeTab === i} aria-controls={`tabpanel-${tab.id}`} id={`tab-${tab.id}`} onClick={() => handleTabChange(i)} className={`relative px-4 md:px-6 py-2.5 text-sm font-medium whitespace-nowrap transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 rounded-lg mr-1 ${activeTab === i ? 'text-[#1C1F23] bg-[#B08D57]' : 'text-white/50 hover:text-white/80 hover:bg-white/8 bg-white/5'}`} data-id="element-173">
                {tab.label}
              </button>)}
          </div>
        </motion.div>

        {/* Tab Panels with Carousel */}
        <AnimatePresence mode="wait" data-id="element-174">
          <motion.div key={activeTab} id={`tabpanel-${activeSection.id}`} role="tabpanel" aria-labelledby={`tab-${activeSection.id}`} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.3,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-175">
            {/* Carousel Container */}
            <div className="relative" data-id="element-176">
              {/* Navigation Arrows */}
              <button onClick={handlePrev} disabled={carouselIndex === 0} aria-label="Previous card" className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-6 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50" data-id="element-177">
                <ChevronLeftIcon className="w-5 h-5 md:w-6 md:h-6" data-id="element-178" />
              </button>

              <button onClick={handleNext} disabled={carouselIndex === maxIndex} aria-label="Next card" className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-6 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50" data-id="element-179">
                <ChevronRightIcon className="w-5 h-5 md:w-6 md:h-6" data-id="element-180" />
              </button>

              {/* Carousel Track */}
              <div ref={carouselRef} className="overflow-hidden" tabIndex={0} role="region" aria-label="Case studies carousel" data-id="element-181">
                <motion.div drag="x" dragConstraints={{
                left: 0,
                right: 0
              }} dragElastic={0.1} onDragStart={(_, info) => setDragStartX(info.point.x)} onDragEnd={handleDragEnd} animate={{
                x: `${translateX}%`
              }} transition={{
                type: 'spring',
                stiffness: 300,
                damping: 30
              }} className="flex" style={{
                gap: cardsPerView === 1 ? '0' : '1.5%'
              }} data-id="element-182">
                  {activeSection.studies.map((study, i) => <div key={study.id} className="flex-shrink-0" style={{
                  width: `${cardWidth}%`
                }} data-id="element-183">
                      <CaseStudyCard study={study} index={i} data-id="element-184" />
                    </div>)}
                </motion.div>
              </div>

              {/* Peek fade on right edge (desktop only) */}
              {carouselIndex < maxIndex && <div className="hidden md:block absolute top-0 right-0 bottom-0 w-16 bg-gradient-to-l from-neutral-950 to-transparent pointer-events-none" data-id="element-185" />}
            </div>

            {/* Carousel Controls */}
            <div className="mt-8 flex items-center justify-center gap-6" data-id="element-186">
              {/* Dot Indicators */}
              <div className="flex items-center gap-2" data-id="element-187">
                {Array.from({
                length: maxIndex + 1
              }).map((_, i) => <button key={i} onClick={() => setCarouselIndex(i)} aria-label={`Go to position ${i + 1}`} className={`w-2 h-2 rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 ${i === carouselIndex ? 'bg-[#B08D57] w-6' : 'bg-white/20 hover:bg-white/40'}`} data-id="element-188" />)}
              </div>

              {/* Card Counter */}
              <div className="text-white/40 text-sm font-medium" data-id="element-189">
                {carouselIndex + 1} of {maxIndex + 1}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* View All Button */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} className="mt-12 flex justify-center" data-id="element-190">
          <a href="#" // TODO: Replace with actual Behance profile URL
        target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white/70 hover:text-white hover:border-white/50 text-sm font-medium transition-all duration-200 hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30" data-id="element-191">
            View All Case Studies on Behance
            <ArrowRightIcon className="w-4 h-4" data-id="element-192" />
          </a>
        </motion.div>
      </div>
    </section>;
};